import { buildRidgeVentProfile, buildRidgeCapProfile } from "building-generation/elements/roofs/roofRidges";
import { getLastSlope } from "building-generation/elements/roofs/roofSlopes";
import { addTruncatedRidgeRails } from "building-generation/elements/roofs/roofTruncation";
import { DEFAULT_ROOFING_MATERIAL_EXTENSION } from "building-types/roofs/roofConstants";
import type {
  RoofVolumeGenerationStageInput,
  RoofVolumeGenerationStageResult,
} from "building-types/roofVolumes/roofVolumeGenerationTypes";
import type { RidgeRail } from "building-types/settings/roofTypes";
import { GableRoofType } from "building-types/settings/settingsTypesEnum";
import { ridgeRailNew, roofRailsNew, roofRails2DNew } from "doc/utils/roofTools";
import { planeNewXY } from "geom/plane";
import { polyLine2ByTwoPoints } from "geom/polyLine2";
import { polyLine3OnPlane, polyLine3ExtendBothEnds, polyLine3Copy } from "geom/polyLine3";

/**
 * Generates the 3D rails and updates the 2D rails (for taking into account the extensions)
 * for a multi-gable roof
 */
export function generateRoofVolumeRails({
  roofVolume,
  roofVolumeOptions,
}: RoofVolumeGenerationStageInput): RoofVolumeGenerationStageResult {
  const { basePlane, baseHeight, roofType, leftSideSlopes, rails2D, peakHeight, verticalRails, extrusion } = roofVolume;
  const { roofInputSettings } = roofVolumeOptions;
  const { roofSettings } = roofInputSettings;

  const { leftRail, rightRail, peakRail, peakRailNoExtension } = rails2D;

  const { roofingMaterialExtension = DEFAULT_ROOFING_MATERIAL_EXTENSION, includeRidgeVent } = roofSettings;

  const peakPlane = planeNewXY(0, 0, peakHeight);

  const { isStartTruncated, isEndTruncated } = verticalRails;

  // generate 3D rails
  const leftRail3 = polyLine3OnPlane(leftRail, basePlane);
  const rightRail3 = polyLine3OnPlane(rightRail, basePlane);
  const peakRail3 = polyLine3OnPlane(peakRail, peakPlane);
  const peakRailNoExtension3 = polyLine3OnPlane(peakRailNoExtension, peakPlane);
  const ridgePeakRail3 = polyLine3OnPlane(peakRail, peakPlane);

  const startRail = polyLine2ByTwoPoints(leftRail[0], leftRail[1], rightRail[0], rightRail[1]);
  const endRail = polyLine2ByTwoPoints(leftRail[2], leftRail[3], rightRail[2], rightRail[3]);

  const startRail3 = polyLine3OnPlane(startRail, basePlane);
  const endRail3 = polyLine3OnPlane(endRail, basePlane);

  // ridges
  const ridgeRails: RidgeRail[] = [];

  if (roofType === GableRoofType.DEFAULT) {
    const lastLeftSlope = getLastSlope(leftSideSlopes).slope;
    const lastRightSlope = getLastSlope(leftSideSlopes).slope;

    ridgeRails.push(
      ridgeRailNew(
        polyLine3ExtendBothEnds(polyLine3Copy(ridgePeakRail3), roofingMaterialExtension, roofingMaterialExtension),
        buildRidgeVentProfile(lastLeftSlope, lastRightSlope),
        buildRidgeCapProfile(lastLeftSlope, lastRightSlope, roofSettings),
        lastLeftSlope,
        lastRightSlope,
        baseHeight,
        true,
        !!includeRidgeVent,
      ),
    );
  }

  if (isStartTruncated) {
    addTruncatedRidgeRails({
      peakRail,
      verticalRails,
      extrusion,
      ridgeRails,
      baseHeight,
      endSide: false,
      roofSettings,
    });
  }

  if (isEndTruncated) {
    addTruncatedRidgeRails({
      peakRail,
      verticalRails,
      extrusion,
      ridgeRails,
      baseHeight,
      endSide: true,
      roofSettings,
    });
  }

  const rails = roofRailsNew(leftRail3, rightRail3, startRail3, endRail3, peakRail3, peakRailNoExtension3, ridgeRails);
  const newRails2D = roofRails2DNew(leftRail, rightRail, startRail, endRail, peakRail, peakRailNoExtension);

  return {
    roofVolume: {
      rails2D: newRails2D,
      rails,
    },
  };
}
